<template>
  <v-card>
    <v-card-title class="primary white--text">
      <v-row align="center">
        <v-col>
          <span class="headline">
            {{ title }}
          </span>
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions @close="handleClose" hideRefresh/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row justify="space-between">
        <v-col cols="12" md="4">
          <v-text-field
          :readonly="nameDisabled"
          ref="sheetName"
          v-model="rateSheetDefinition.name"
          :label="$t('name')"
          :maxLength="maxNameLength"
          :rules="nameRules"
          @mousedown="openNameEditor"
          :style="nameFieldStyle">
            <template #append>
              <Icon
              v-if="canEditName"
              icon="mdi-pencil"
              :tooltipText="$t('editName')"
              margin=""
              :small="false"
              @icon-clicked="openNameEditor"/>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="6" md="4" v-if="nameIsValid">
          <v-select
          class="rate-sheet-definition-version-selector"
          :items="versionsForSelect"
          :label="$t('version')"
          v-model="selectedVersion"
          @change="doAnimationForVersionSelected"
          return-object
          :item-value="(item) => item.versionedRateSheetId">
            <template #append-outer>
              <Icon
              icon="mdi-plus"
              :small="false"
              :tooltipText="$t('newVersion')"
              :disabled="disableNewVersion"
              @icon-clicked="createNewVersionClicked"/>
            </template>

            <template #item="{ item }">
              <v-row no-gutters align="center">
                <v-col cols="auto">
                  <Icon
                  v-if="item.status"
                  :iconColor="item.status.color"
                  :icon="item.status.icon"
                  :tooltipText="$t(item.status.name)"
                  :small="false"/>
                </v-col>

                <v-col>
                  <span>{{ getTextForItem(item) }}</span>
                </v-col>
              </v-row>
            </template>

            <template #selection="{ item }">
              <v-row no-gutters align="center">
                <v-col cols="auto">
                  <Icon
                  v-if="item.status"
                  :iconColor="item.status.color"
                  :icon="item.status.icon"
                  :tooltipText="$t(item.status.name)"
                  :small="false"/>
                </v-col>

                <v-col>
                  <span>{{ getTextForItem(item) }}</span>
                </v-col>

                <v-col cols="auto">
                  <Icon
                  :icon="'mdi-pencil'"
                  :small="false"
                  v-if="allowEditVersion(item)"
                  @icon-clicked="editVersion(item)"/>

                  <Icon
                  v-if="allowDeleteVersion(item)"
                  :icon="'mdi-delete-forever'"
                  :small="false"
                  @icon-clicked="deleteVersionClicked(item)"/>
                </v-col>
              </v-row>
            </template>
          </v-select>
        </v-col>

        <v-col cols="12" v-if="nameIsValid" :class="ratesCardClass">
          <DetailCard :title="$t('rates')">
            <template #title.append>
              <Icon
              v-if="!disableCrud"
              icon="mdi-plus"
              :tooltipText="$t('newRate')"
              :disabled="disableCrud"
              @icon-clicked="newClicked()"
              margin=""
              :small="false"
              tabindex="0"/>
            </template>

            <RateSheetLineTableFilterable
            :lines="lineItems"
            :accountId="account.accountId"
            :loading="loading || !initialized"
            :rateSheetInteractionMode="RateSheetInteractionMode.RateSheetDefinition.Edit"
            :crud="crud"
            @edit="editLine"
            @delete="deleteLine"
            @rates-edited="handleRatesEdited"
            ></RateSheetLineTableFilterable>
          </DetailCard>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="py-4 mx-0">
      <v-row justify="end">
        <v-col cols="auto">
          <v-btn
          color="primary"
          :disabled="!isValid"
          @click="handleConfirm">
            <span>
              {{this.$t('confirm')}}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
    <Dialog
    :stateId="dialogId"
    @dialog-closing="handleCancelEdit"
    @click:outside="handleCancelEdit"
    @close="handleCancelEdit">
      <AccountRateSheetDefinitionLineForm
      v-if="mutatingLine"
      :rateSheetDefinitionLine="focusedRateSheetDefinitionLine"
      :account="account"
      :rateSheetDefinition="rateSheetDefinition"
      :selectedVersion="selectedVersion"
      :propSpeciesGroup="getSpeciesGroupByLabel(filterBySpeciesGroup)"
      @save-and-close="handleSaveLineAndClose"
      @save="handleSaveLine"
      @cancel="handleCancelEdit" />

      <RateSheetDefinitionVersionCreator
      v-if="createVersionMenuOpen"
      :versions="rateSheetDefinition.rates"
      :propCreateFromVersion="selectedVersion"
      @creation-canceled="versionCreationCanceled"
      @creation-confirmed="versionCreationConfirmed">
      </RateSheetDefinitionVersionCreator>

      <RateSheetDefinitionVersionEditor
      v-if="editVersionMenuOpen"
      :versions="rateSheetDefinition.rates"
      :propVersion="selectedVersion"
      @edit-canceled="versionEditCanceled"
      @edit-confirmed="versionEditConfirmed">
      </RateSheetDefinitionVersionEditor>

      <ConfirmDialog
      v-if="versionForDeletion"
      :title="$t('deleteVersion')"
      :body="$t('deleteVersionBody')"
      :confirmText="$t('delete')"
      :cancelText="$t('cancel')"
      :color="'error'"
      @confirm="deleteVersion"
      @cancel="closeDialog"
      />

      <ConfirmDialog
        v-if="confirmDialog"
        :title="$t('closeDialogWithoutSavingTitle')"
        :body="$t('closeDialogWithoutSavingWarning')"
        :confirmText="$t('proceed')"
        :cancelText="$t('cancel')"
        width="30vw"
        @cancel="cancelConfirmDialog"
        @confirm="confirmCloseDialog"
      />

      <RateSheetDefinitionNameEditor
      v-if="editNameMenuOpen"
      :propName="rateSheetDefinition.name"
      @edit-canceled="nameEditCanceled"
      @edit-confirmed="nameEditConfirmed">
      </RateSheetDefinitionNameEditor>
    </Dialog>
  </v-card>
</template>
<script>
import { rateSheetDefinitionLineHeaders } from '@/headers/Account'
import fieldRules from '@/utils/rules'
import {
  uniqueDialogId, dataTableItemsPerPage,
  dataTableDefaultItemsPerPage, tableHeight
} from '@/utils/componentHelpers'
import { mapActions, mapGetters } from 'vuex'
import { formatMoney } from '@/utils/NumericMutations.js'
import {
  RateSheetDefinitionBuilder, RateSheetDefinitionRequest,
  VersionedRateSheetBuilder, VersionedRateSheetRequest,
  rateSheetDefinitionLineSorter, VersionedRateSheetStatus,
  assignVersionedRateSheetStatus
} from './RateSheetDefinitions'
import { formatDate } from '@/utils/DateFormatter.js'
import { cloneDeep, uniq } from 'lodash'
import { getSpeciesGroupByLabel, getSpeciesGroupTooltip } from '@/components/account/account-scaling/species-groups/SpeciesGroups'
import { getLengthGroupTooltip } from '@/components/account/account-scaling/length-groups/LengthGroups'
import { getDiameterGroupTooltip } from '@/components/account/account-scaling/diameter-groups/DiameterGroups'
import moment from 'moment'
import { RateSheetInteractionMode } from '@/components/activity/rate-sheet/RateSheet'

const MAX_NAME_LENGTH = 32
const LINE_EDITOR_WIDTH = '500px'

export default {
  name: 'AccountRateSheetDefinitionForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    AccountRateSheetDefinitionLineForm: () => import('@/components/account/account-scaling/rate-sheet-definitions/AccountRateSheetDefinitionLineForm.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    RateSheetDefinitionVersionCreator: () => import('@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitionVersionCreator.vue'),
    RateSheetDefinitionVersionEditor: () => import('@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitionVersionEditor.vue'),
    RateSheetLineTableFilterable: () => import('@/components/activity/rate-sheet/RateSheetLineTableFilterable.vue'),
    RateSheetDefinitionNameEditor: () => import('@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitionNameEditor.vue')
  },

  props: {
    propRateSheetDefinition: {
      type: Object,
      required: false,
      default: undefined
    },
    account: {
      type: Object,
      required: false,
      default: null
    },
    versions: {
      type: Array,
      required: false,
      default: null
    },
    version: {
      type: Object,
      required: false,
      default: null
    },
    crud: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    RateSheetInteractionMode,
    dataTableDefaultItemsPerPage,
    dataTableItemsPerPage,
    initialized: false,
    loading: false,
    rules: fieldRules.rules,
    maxNameLength: MAX_NAME_LENGTH,
    dialogId: uniqueDialogId('account-rate-sheet-definition-form'),
    rateSheetDefinition: new RateSheetDefinitionBuilder(null, null,
      [new VersionedRateSheetBuilder()]),
    mutatingLine: false,
    focusedRateSheetDefinitionLine: null,
    selectedVersion: null,
    datePickerRerenderTrigger: true,
    filterBySpeciesGroup: null,
    confirmExit: false,
    createVersionMenuOpen: false,
    editVersionMenuOpen: false,
    editNameMenuOpen: false,
    newVersion: {
      effectiveDate: moment(),
      rateSheetDefinitionLines: []
    },
    tableIsFading: false,
    versionForDeletion: null,
    rateTableId: uniqueDialogId('account-rate-sheet-definition-table'),
    tableHeight: 'auto',
    pendingEditedLines: [],
    confirmDialog: false
  }),

  async mounted () {
    await this.initialize()
  },

  computed: {
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions']),
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('account-scaling', ['accountSpeciesGroups', 'accountLengthGroups', 'accountDiameterGroups']),

    accountId () {
      return this.account?.accountId || null
    },

    title () {
      return this.$t(this.rateSheetDefinition?.rateSheetDefinitionId
        ? 'editingRateSheetDefinitionFor'
        : 'creatingRateSheetDefinitionFor',
      { name: this.account.name })
    },

    headers () {
      return rateSheetDefinitionLineHeaders(true)
    },

    nameRules () {
      return [this.rules.required]
    },

    isValid () {
      return this.nameIsValid && this.rateSheetDefinitionLinesAreValid
    },

    nameIsValid () {
      return !!this.rateSheetDefinition?.name &&
      this.rateSheetDefinition?.name?.length <= MAX_NAME_LENGTH &&
      this.rateSheetDefinition?.name?.length > 0
    },

    rateSheetDefinitionLinesAreValid () {
      return this.selectedVersion?.rateSheetDefinitionLines.length > 0
    },

    lineItems () {
      if (!this.selectedVersion) return []
      const rateSheetDefinitionLines = this.selectedVersion?.rateSheetDefinitionLines
        .toSorted(this.lineSorter)
      return rateSheetDefinitionLines
    },

    lineItemsFiltered () {
      if (!this.filterBySpeciesGroup) return this.lineItems
      const filtered = this.lineItems.filter(item => this.isBlank(item) ||
      item?.speciesGroup === this.filterBySpeciesGroup ||
      !item?.speciesGroup)
      return filtered
    },

    speciesGroupSelectionOptions () {
      if (!this.selectedVersion?.rateSheetDefinitionLines?.length) return []
      return uniq(this.selectedVersion.rateSheetDefinitionLines
        .filter(this.speciesGroupFilterPredicate)
        .map(this.speciesGroupMapper))
    },

    versionsForSelect () {
      return this.rateSheetDefinition.rates
    },

    disableCrud () {
      return this.selectedVersion.status?.id === VersionedRateSheetStatus.Expired.id
    },

    nameDisabled () { // TODO MK: Change when server supports PUT
      return this.rateSheetDefinition.rateSheetDefinitionId != null
    },

    disableNewVersion () {
      return this.versionsForSelect.some(v => !v.versionedRateSheetId || v?.status?.id === VersionedRateSheetStatus.Inactive.id)
    },

    ratesCardClass () {
      return `can-fade-translate-down ${this.tableIsFading ? 'fading-translate-down' : 'not-fading-translate-down'}`
    },

    activeRateSheetVersion () {
      return this.rateSheetDefinition.rates.find(v => v.status?.id === VersionedRateSheetStatus.Active.id)
    },

    canEditName () {
      return !!this.rateSheetDefinition?.rateSheetDefinitionId
    },

    nameFieldStyle () {
      return this.canEditName ? { cursor: 'pointer' } : {}
    }
  },

  watch: {
    selectedVersion () {
      this.triggerDateRerender()
    },

    speciesGroupSelectionOptions (s) {
      if (s?.length === 1) {
        this.filterBySpeciesGroup = s[0]
      } else {
        this.filterBySpeciesGroup = null
      }
    },

    '$vuetify.breakpoint.height' () { this.assignTableHeight() },
    '$vuetify.breakpoint.width' () { this.assignTableHeight() },
    'topDialogState.fullscreen' () { this.assignTableHeightWithDelay() },
    lineItemsFiltered () { this.assignTableHeightWithDelay() }
  },

  methods: {
    formatMoney,
    formatDate,
    getSpeciesGroupByLabel,
    getSpeciesGroupTooltip,
    getLengthGroupTooltip,
    getDiameterGroupTooltip,

    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('rate-sheet-definition', ['fetchVersionedRateSheetDefinition', 'fetchAccountRateSheetDefinition',
      'createAccountRateSheetDefinition', 'updateAccountRateSheetDefinition', 'deleteAccountRateSheetDefinition',
      'createVersionedRateSheetDefinition', 'patchVersionedRateSheetDefinition', 'deleteVersionedRateSheetDefinition',
      'createAccountRateSheetDefinitionLine', 'updateAccountRateSheetDefinitionLine', 'deleteAccountRateSheetDefinitionLine',
      'patchAccountRateSheetDefinitionName']),
    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'fetchAccountDiameterGroups', 'fetchAccountSpeciesGroups']),

    async refreshVersion () {
      this.loading = true
      const vsnResponse = await this.fetchVersionedRateSheetDefinition({
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId
      })
      this.assignSelectedVersion(vsnResponse)
      this.loading = false
    },

    assignSelectedVersion (vsnResponse) {
      vsnResponse.forEach(freshVersion => {
        const idx = this.rateSheetDefinition.rates.findIndex(
          v => v.versionedRateSheetId === freshVersion.versionedRateSheetId
        )
        if (idx !== -1) {
          this.rateSheetDefinition.rates[idx] = freshVersion
        }
      })
      assignVersionedRateSheetStatus(this.rateSheetDefinition.rates)
      if (vsnResponse.length === 1) {
        this.selectedVersion = vsnResponse[0]
      } else {
        this.selectedVersion = vsnResponse.find(v => v.versionedRateSheetId === this.selectedVersion.versionedRateSheetId)
      }
    },

    focus (refString) {
      this.$nextTick(_ => {
        this.$refs[refString].focus()
      })
    },

    async initialize () {
      if (this.propRateSheetDefinition) {
        this.initializeRateSheetDefinition()
        this.selectedVersion = cloneDeep(this.version)
      } else {
        this.rateSheetDefinition.rates = [this.newVersion]
        this.selectedVersion = this.newVersion
      }
      this.focus('sheetName')
      this.assignTableHeightWithDelay()
      await Promise.all([
        this.fetchAccountLengthGroups(this.accountId),
        this.fetchAccountDiameterGroups(this.accountId),
        this.fetchAccountSpeciesGroups(this.accountId)
      ])
      this.initialized = true
    },

    initializeRateSheetDefinition () {
      this.rateSheetDefinition = cloneDeep(this.propRateSheetDefinition)
      this.rateSheetDefinition.account = this.account
      this.rateSheetDefinition.rates = cloneDeep(this.versions)
    },

    newClicked () {
      this.mutatingLine = true
      this.$emit('editing-ratesheet')
      this.openDialog(LINE_EDITOR_WIDTH)
    },

    openDialog (width = '400px') {
      this.openOrUpdateDialog({ id: this.dialogId, width })
    },

    closeDialog () {
      this.editVersionMenuOpen = false
      this.createVersionMenuOpen = false
      this.editNameMenuOpen = false
      this.mutatingLine = false
      this.versionForDeletion = null
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async handleConfirm () {
      await this.saveEditedRates()
      this.$emit('close', this.rateSheetDefinition)
    },

    async handleClose () {
      if (this.pendingEditedLines.length > 0) {
        this.confirmDialog = true
        this.openDialog()
        return
      }
      this.$emit('close', this.rateSheetDefinition)
    },

    cancelConfirmDialog () {
      this.confirmDialog = false
      this.closeDialog()
    },

    async confirmCloseDialog () {
      this.confirmDialog = false
      this.$emit('close', this.rateSheetDefinition)
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    handleSaveLineAndClose (line) {
      this.handleSaveLine(line)
      this.closeDialog()
    },

    async handleSaveLine (line) {
      const createNewDefn = this.rateSheetDefinition.rateSheetDefinitionId == null
      const createNewVsn = this.selectedVersion.versionedRateSheetId == null
      if (createNewDefn) {
        await this.handleDefinitionCreation(line)
      } else if (createNewVsn) {
        await this.handleVersionCreation(line)
      } else {
        await this.handleLineMutation(line)
      }
      await this.refreshVersion()
    },

    async handleDefinitionCreation (line) {
      const defnResponse = await this.createAccountRateSheetDefinition(this.getDefnRequest(line))
      this.rateSheetDefinition = { ...this.rateSheetDefinition, ...defnResponse }
      this.rateSheetDefinition.rates[0].rateSheetDefinitionLines = [line]
      this.$emit('new-defn-created', this.rateSheetDefinition)
    },

    getDefnRequest (line) {
      return new RateSheetDefinitionRequest(
        this.rateSheetDefinition.name,
        this.account.accountId,
        [new VersionedRateSheetBuilder(
          this.selectedVersion.effectiveDate,
          [line]
        )]
      )
    },

    async handleVersionCreation (line) {
      const vsn = await this.createVersionedRateSheetDefinition(this.getVsnRequest(line))
      this.selectedVersion.versionedRateSheetId = vsn.versionedRateSheetId
      assignVersionedRateSheetStatus(this.rateSheetDefinition.rates)
    },

    getVsnRequest (line) {
      return {
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        ...new VersionedRateSheetBuilder(
          this.selectedVersion.effectiveDate,
          [line]
        )
      }
    },

    async handleLineMutation (line) {
      const { rateSheetDefinitionLineId } = line
      const request = this.mapperLineToServerLine(line)
      rateSheetDefinitionLineId
        ? await this.updateAccountRateSheetDefinitionLine(request)
        : await this.createAccountRateSheetDefinitionLine(request)
    },

    mapperLineToServerLine (line) {
      return {
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        versionedRateSheetId: this.selectedVersion.versionedRateSheetId,
        rateSheetDefinitionLineId: line.rateSheetDefinitionLineId,
        rate: line.rate,
        speciesGroupId: line.speciesGroupId ?? line.speciesGroup?.speciesGroupId,
        lengthGroupId: line.lengthGroupId ?? line.lengthGroup?.lengthGroupId,
        diameterGroupId: line.diameterGroupId ?? line.diameterGroup?.diameterGroupId,
        gradeId: line.gradeId ?? line.grade?.gradeId,
        endUseSortId: line.endUseSortId ?? line.endUseSort?.endUseSortId
      }
    },

    isBlank (lineItem) {
      return lineItem.rate == null
    },

    async deleteLine ({ line, unformattedLine }) {
      await this.deleteAccountRateSheetDefinitionLine({
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        versionedRateSheetId: this.selectedVersion.versionedRateSheetId,
        rateSheetDefinitionLineId: line.rateSheetDefinitionLineId
      })
      this.refreshVersion()
    },

    editLine ({ line, unformattedLine }) {
      this.focusedRateSheetDefinitionLine = { ...line, ...unformattedLine }
      this.mutatingLine = true
      this.$emit('editing-ratesheet')
      this.openDialog(LINE_EDITOR_WIDTH)
    },

    handleCancelEdit () {
      this.mutatingLine = false
      this.focusedRateSheetDefinitionLine = null
      this.createVersionMenuOpen = false
      this.confirmExit = false
      this.closeDialog()
    },

    getTextForItem (item) {
      return formatDate(item.effectiveDate)
    },

    triggerDateRerender () {
      this.datePickerRerenderTrigger = false
      this.$nextTick(() => {
        this.datePickerRerenderTrigger = true
      })
    },

    createNewVersionClicked () {
      this.createFromVersion = this.selectedVersion
      this.createVersionMenuOpen = true
      this.openDialog(LINE_EDITOR_WIDTH)
    },

    async versionCreationConfirmed (version) {
      this.createVersionMenuOpen = false
      this.closeDialog()
      if (version.rateSheetDefinitionLines.length > 0) {
        const vsnRequest = {
          ...version,
          rateSheetDefinitionLines: version.rateSheetDefinitionLines.map(this.mapperLineToServerLine)
        }
        const vsn = await this.createVersion(vsnRequest)
        this.selectedVersion = version
        this.selectedVersion.versionedRateSheetId = vsn.versionedRateSheetId
        this.refreshVersion()
        this.rateSheetDefinition.rates.push(this.selectedVersion)
        assignVersionedRateSheetStatus(this.rateSheetDefinition.rates)
        this.doAnimationForVersionSelected()
      } else {
        this.rateSheetDefinition.rates.push(version)
        this.selectedVersion = version
      }
    },

    async createVersion (version) {
      return await this.createVersionedRateSheetDefinition({
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        ...new VersionedRateSheetRequest(
          version.effectiveDate,
          version.rateSheetDefinitionLines
        )
      })
    },

    versionCreationCanceled () {
      this.createVersionMenuOpen = false
      this.closeDialog()
    },

    async versionEditConfirmed (version) {
      this.editVersionMenuOpen = false
      this.closeDialog()
      if (!this.selectedVersion.versionedRateSheetId) {
        this.selectedVersion.effectiveDate = version.effectiveDate
        return
      }
      if (this.selectedVersion.status?.id !== VersionedRateSheetStatus.Inactive.id) return
      try {
        await this.patchVersionEffectiveDate(version.effectiveDate)
        await this.refreshVersion()
      } catch (e) {
        console.error(e)
      }
    },

    async patchVersionEffectiveDate (effectiveDate) {
      const body = [{
        op: 'add',
        path: '/effectiveDate',
        value: effectiveDate
      }]
      await this.patchVersionedRateSheetDefinition({
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        versionedRateSheetId: this.selectedVersion.versionedRateSheetId,
        body
      })
    },

    versionEditCanceled () {
      this.editVersionMenuOpen = false
      this.closeDialog()
    },

    allowDeleteVersion (item) {
      return this.rateSheetDefinition.rates.length > 1 && item?.status?.id === VersionedRateSheetStatus.Inactive.id
    },

    allowEditVersion (item) {
      return !item.versionedRateSheetId || item?.status?.id === VersionedRateSheetStatus.Inactive.id
    },

    async deleteVersion () {
      try {
        await this.sendDeleteRequest()
        this.versionForDeletion = null
        this.rateSheetDefinition = await this.fetchRateSheetDefinition()
        assignVersionedRateSheetStatus(this.rateSheetDefinition.rates)
        this.selectedVersion = this.activeRateSheetVersion
        this.doAnimationForVersionSelected()
        await this.refreshVersion()
      } catch (e) {
        console.error(e)
      } finally {
        this.closeDialog()
      }
    },

    async sendDeleteRequest () {
      await this.deleteVersionedRateSheetDefinition({
        accountId: this.account.accountId,
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        versionedRateSheetId: this.versionForDeletion.versionedRateSheetId
      })
    },

    async fetchRateSheetDefinition () {
      const res = await this.fetchAccountRateSheetDefinition({
        rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
        accountId: this.account.accountId
      })
      return {
        ...res,
        product: {
          name: res.productName,
          productId: res.productId
        }
      }
    },

    editVersion (item) {
      this.selectedVersion = item
      this.editVersionMenuOpen = true
      this.openDialog(LINE_EDITOR_WIDTH)
    },

    doAnimationForVersionSelected () {
      this.tableIsFading = true
      setTimeout(() => {
        this.tableIsFading = false
      }, 150)
    },

    speciesGroupFilterPredicate ({ speciesGroup }) {
      return !speciesGroup || typeof speciesGroup === 'string' || typeof speciesGroup?.label === 'string'
    },

    speciesGroupMapper ({ speciesGroup }) {
      if (!speciesGroup) return this.$t('notSpecified')
      if (typeof speciesGroup === 'string') {
        return speciesGroup
      } else if (typeof speciesGroup?.label === 'string') {
        return speciesGroup.label
      } else {
        return undefined
      }
    },

    deleteVersionClicked (item) {
      this.openDialog()
      this.versionForDeletion = item
    },

    openNameEditor () {
      if (!this.canEditName) return
      this.editNameMenuOpen = true
      this.openDialog(LINE_EDITOR_WIDTH)
    },

    nameEditCanceled () {
      this.editNameMenuOpen = false
      this.closeDialog()
    },

    async nameEditConfirmed (newName) {
      const body = [{
        op: 'add',
        path: '/name',
        value: newName
      }]
      try {
        await this.patchAccountRateSheetDefinitionName({
          accountId: this.account.accountId,
          rateSheetDefinitionId: this.rateSheetDefinition.rateSheetDefinitionId,
          body: body
        })
        this.rateSheetDefinition.name = newName
        this.editNameMenuOpen = false
        this.closeDialog()
      } catch (e) {
        console.error(e)
      }
    },

    assignTableHeight () {
      if (!this.initialized || !this.selectedVersion) {
        this.tableHeight = 'auto'
      } else {
        return tableHeight({
          tableElementId: this.rateTableId,
          dataLength: this.lineItemsFiltered.length,
          offset: 400,
          headersLength: this.headers.length
        })
      }
    },

    assignTableHeightWithDelay (delay = 128) {
      setTimeout(() => {
        this.tableHeight = this.assignTableHeight()
      }, delay)
    },

    lineSorter (a, b) {
      const toMapped = (line) => ({
        speciesGroup: {
          label: line.speciesGroupLabel,
          speciesGroupId: line.speciesGroupId
        },
        lengthGroup: {
          label: line.lengthGroupLabel,
          lengthGroupId: line.lengthGroupId,
          logLengths: this.accountLengthGroups?.find(lg => lg.lengthGroupId === line.lengthGroupId)?.logLengths
        },
        diameterGroup: {
          label: line.diameterGroupLabel,
          diameterGroupId: line.diameterGroupId,
          lowerBound: this.accountDiameterGroups?.find(dg => dg.diameterGroupId === line.diameterGroupId)?.lowerBound,
          upperBound: this.accountDiameterGroups?.find(dg => dg.diameterGroupId === line.diameterGroupId)?.upperBound
        },
        grade: {
          name: line.gradeName,
          gradeId: line.gradeId
        },
        endUseSort: {
          name: line.endUseSortName,
          endUseSortId: line.endUseSortId
        }
      })
      return rateSheetDefinitionLineSorter(toMapped(a), toMapped(b))
    },

    handleRatesEdited (editedLines) {
      const filtered = editedLines.filter(editedLine => {
        const original = this.lineItems.find(
          l => l.rateSheetDefinitionLineId === editedLine.rateSheetDefinitionLineId
        )
        return original && original.rate !== editedLine.rate
      })
      this.pendingEditedLines = filtered
      this.$emit('editing-ratesheet')
    },

    async saveEditedRates () {
      if (!this.pendingEditedLines.length) return
      this.loading = true
      await Promise.all(
        this.pendingEditedLines.map(line => {
          const request = this.mapperLineToServerLine(line)
          return this.updateAccountRateSheetDefinitionLine(request)
        })
      )
      await this.refreshVersion()
      this.loading = false
      this.pendingEditedLines = []
    }
  }
}
</script>

<style>
.rate-sheet-definition-version-selector .v-select__selections input {
  max-width: 0px;
}

</style>
